import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr } from "@/lib/api";
import { usePageMeta } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";

function useCountdown(iso) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, new Date(iso).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
}

const loadCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) { resolve(); return; }
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve;
  s.onerror = () => reject(new Error("Could not load Razorpay. Check your connection and try again."));
  document.body.appendChild(s);
});

function Summary({ o }) {
  return (
    <div className="bg-white border border-[#e3e3e3] p-6 mt-8 text-left">
      <ul className="space-y-2 text-sm">
        {o.items.map((i) => <li key={i.product_id} className="flex justify-between gap-3"><span>{i.artist} – {i.title} ({i.format}) × {i.qty}</span><span className="font-arial">{inr(i.price * i.qty)}</span></li>)}
      </ul>
      <div className="border-t border-[#e5e5e5] mt-4 pt-3 font-arial text-sm space-y-1">
        {o.discount > 0 && <div className="flex justify-between text-[#1f7a3a]" data-testid="pay-discount"><span>Coupon {o.coupon_code}</span><span>−{inr(o.discount)}</span></div>}
        <div className="flex justify-between"><span>Delivery</span><span>{o.shipping ? inr(o.shipping) : "Free"}</span></div>
        <div className="flex justify-between font-bold text-base"><span>Total</span><span className="text-red" data-testid="pay-total">{inr(o.total)}</span></div>
      </div>
    </div>
  );
}

export default function Pay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const left = useCountdown(o?.hold_expires_at);
  usePageMeta("Payment | Vinyl Record Vault");

  useEffect(() => {
    api.get(`/orders/${id}`, { params: { email } }).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e, "Order not found")));
  }, [id, email]);

  useEffect(() => {
    api.get("/payments/config").then((r) => setLive(r.data.razorpay)).catch(() => setLive(false));
  }, []);

  const refresh = () => api.get(`/orders/${id}`, { params: { email } }).then((r) => setO(r.data));
  const fail = (e) => {
    toast.error(errMsg(e, e?.message || "Payment failed"));
    refresh();
    setBusy(false);
  };

  const payRazorpay = async () => {
    const [, r] = await Promise.all([loadCheckout(), api.post(`/orders/${id}/razorpay`, { email })]);
    const d = r.data;
    const rz = new window.Razorpay({
      key: d.key_id, amount: d.amount, currency: d.currency, order_id: d.rzp_order_id,
      name: "Vinyl Record Vault", description: `Order ${d.order_number}`, image: `${window.location.origin}/logo.png`,
      prefill: { name: d.name, email: d.email, contact: `+91${String(d.phone || "").replace(/\D/g, "").slice(-10)}` }, theme: { color: "#DC1F26" },
      handler: async (res) => {
        try {
          const v = await api.post(`/orders/${id}/razorpay/verify`, { email, ...res });
          setO(v.data);
          setBusy(false);
        } catch (e) { fail(e); }
      },
      modal: { ondismiss: () => setBusy(false) },
    });
    rz.on("payment.failed", (res) => toast.error(res?.error?.description || "Payment failed. Please try again."));
    rz.open();
  };

  const pay = async () => {
    setBusy(true);
    try {
      if (live) {
        await payRazorpay();
      } else {
        setO((await api.post(`/orders/${id}/pay`, { email })).data);
        setBusy(false);
      }
    } catch (e) { fail(e); }
  };

  if (err) return <div className={`${WRAP} py-32 text-center`} data-testid="pay-error">{err}</div>;
  if (!o) return <div className={`${WRAP} py-16`}><div className="h-80 animate-pulse bg-white/60" /></div>;
  const expired = o.payment_status === "expired" || (o.payment_status === "pending" && left === 0);
  const mm = String(Math.floor(left / 60000)).padStart(2, "0");
  const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");

  return (
    <div className={`${WRAP} pt-14 max-w-2xl text-center`}>
      {o.payment_status === "paid" ? (
        <div data-testid="pay-success">
          <CheckCircle2 className="w-14 h-14 text-[#1f7a3a] mx-auto" strokeWidth={1.5} />
          <h1 className="font-head font-extrabold text-4xl tracking-tight mt-4">Thank you, {o.customer.name.split(" ")[0]}!</h1>
          <p className="mt-3 text-[#555]">Your payment was received. A confirmation email is on its way to <b>{o.customer.email}</b>.</p>
          <p className="mt-6 label-caps text-[11px] text-[#777]">Order number</p>
          <p className="font-arial font-bold text-3xl text-red mt-1" data-testid="pay-order-number">{o.order_number}</p>
          <Summary o={o} />
          <Link to={`/track?number=${o.order_number}&email=${encodeURIComponent(o.customer.email)}`} className="inline-block mt-8 bg-black text-white px-6 py-3 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26]" data-testid="pay-track-link">TRACK THIS ORDER</Link>
        </div>
      ) : expired ? (
        <div data-testid="pay-expired">
          <AlertTriangle className="w-12 h-12 text-red mx-auto" strokeWidth={1.5} />
          <h1 className="font-head font-extrabold text-3xl mt-4">Payment window expired</h1>
          <p className="mt-3 text-[#555]">Order {o.order_number} wasn't paid within 30 minutes, so the records were released back to the vault.</p>
          <Link to="/shop" className="inline-block mt-8 bg-black text-white px-6 py-3 text-xs font-bold tracking-[0.15em]">BACK TO THE CRATES</Link>
        </div>
      ) : (
        <div data-testid="pay-pending">
          <p className="label-caps text-[11px] text-red">Order {o.order_number}</p>
          <h1 className="font-head font-extrabold text-4xl tracking-tight mt-2">Complete your payment</h1>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#555]" data-testid="pay-countdown"><Clock className="w-4 h-4 text-red" /> Records held for <b className="font-arial">{mm}:{ss}</b></p>
          <Summary o={o} />
          {live === false && (
            <div className="mt-6 border border-dashed border-[#dc1f26] bg-[#fff5f5] text-[#b8161c] text-xs p-3 font-arial" data-testid="pay-test-mode-banner">
              TEST MODE – Razorpay is not connected yet. This button simulates a successful payment.
            </div>
          )}
          <button disabled={busy || live === null} onClick={pay} className="mt-4 w-full bg-black text-white py-4 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26] disabled:opacity-50 transition-colors" data-testid="pay-now-button">
            {busy ? "PROCESSING…" : `PAY ${inr(o.total)} · UPI / CARDS / NETBANKING / WALLETS`}
          </button>
          {live && <p className="mt-3 text-[11px] text-[#777] font-arial" data-testid="pay-secure-note">Secure payment by Razorpay</p>}
        </div>
      )}
    </div>
  );
}
